import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { ObjectBrowser } from '@/components/ObjectBrowser';
import type { BrowserFetcherResult } from '@/components/ObjectBrowser';
import { EntityCard } from '@/components/EntityCard';
import { InitiativeCard } from '@/components/initiatives/InitiativeCard';
import { entitiesApi, initiativesApi } from '@/lib/api';
import type { Entity, ApiInitiative } from '@/lib/api/types';
import type { MapBrowserObject } from '@/components/ObjectMapBrowser';
import { getInitiativeHighlight, INITIATIVE_TIMING_LEGEND } from '@/components/ObjectMapBrowser';
import { useSessionState } from '@/components/SessionState';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { useEntityDetailSheet, buildEntityNavigationList } from '@/lib/hooks/useEntityDetailSheet';
import { useInitiativeDetailSheet, buildInitiativeNavigationList } from '@/lib/hooks/useInitiativeDetailSheet';
import { useSession } from '@/lib/auth-client';
import { DatePickerWithRange } from '@/components/custom/date-picker-with-range';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { ENTITY_ROLE_OPTIONS, INITIATIVE_TAG_OPTIONS, getInitiativeUrl, RelativeDatePresents, formatEventDateRange } from '@sizzlenz/shared';
import { getDirectoryFilterConfigs, DIRECTORY_URL_SYNC_FILTERS, buildDirectoryInitialFilters, syncDirectoryFilters, buildDirectoryApiQuery } from '@/lib/directory-filters';
import { useCountries } from '@/lib/hooks/useCountries';
import { WHATSON_URL_SYNC_FILTERS, getWhatsOnFilterConfigs, buildWhatsOnInitialFilters, syncWhatsOnFilters, buildWhatsOnApiQuery } from '@/lib/whatson-filters';
import { ContentBrowser } from '@/components/content/ContentBrowser';
import { ContentListItem } from '@/components/content/ContentListItem';
import { InspirationFeed, type LockedInspirationFilters } from '@/components/demand/InspirationFeed';
import { INSPIRATION_FILTER_KEYS, type InspirationFilterKey } from '@/components/demand/InspirationFilters';
import { parseQuickFilterPresets } from '@/components/filters/QuickFilters';
import { CollectionGridView } from '@/components/CollectionGridView';
import { ItemGridView } from '@/components/ItemGridView';
import { MembersTab } from '@/components/membership/MembersTab';
import { Markdown } from '@/components/Markdown';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useEarlyPreviewBanner } from '@/components/EarlyPreviewProvider';
import { getFixedChromeOffset } from '@/lib/layout';
import { FollowButton } from '@/components/FollowButton';

// --- Types ---

export interface HubTab {
  id: string;
  title?: string;
  icon?: React.ReactNode;
  tooltip?: string;
  description?: string;
  view: string;
  component?: React.ComponentType;
  /** When true, the tab is hidden and not rendered. */
  disabled?: boolean;
  /** Per-tab context overrides – merged on top of the hub-level context */
  context?: Record<string, any>;
}
export interface HubTagFilterOption {
  /** Tag value(s) sent to the API. Single string or array for grouped tags. */
  value: string | string[];
  /** Display label shown to the user. */
  label: string;
}
export interface HubViewProps {
  id: string;
  title?: string;
  context: Record<string, any>;
  tabs: HubTab[];
  tooltipsDisabled?: boolean;
  content?: React.ReactNode;
  /** Label for the "Back to..." link on content detail pages. */
  backLabel?: string;
  /** URL for the "Back to..." link on content detail pages. */
  backHref?: string;
  /** Follow configuration – when provided, shows FollowButton in the hub header */
  follow?: {
    objectId: number;
    objectType: 'entity' | 'item' | 'initiative' | 'content';
    following: boolean;
  };
  /** Tag filter options displayed globally above tab content. Selected tags are passed to all tab views via context.tags. */
  tagFilter?: HubTagFilterOption[];
  /** Label for the tag filter. Defaults to "Filter". */
  tagFilterLabel?: string;
  /** Horizontal alignment of the tag filter row. Defaults to 'left'. */
  tagFilterAlign?: 'left' | 'center' | 'right';
  /** Controls whether tag filters wrap onto multiple lines or scroll horizontally with fade indicators. Defaults to 'wrap'. */
  tagFilterOverflow?: 'wrap' | 'scroll';
  /**
   * Restricts which tabs show the global tag filter row, keyed by tab `id`.
   * When omitted, the tag filter row is shown on every tab (the default).
   * Provide a list of tab ids (e.g. `['whatson', 'news']`) to only show it
   * while those tabs are active. An empty array hides it on every tab.
   */
  tagFilterTabs?: string[];
}

// --- Map helpers ---

function entityToMapObject(entity: Entity): MapBrowserObject {
  const addressData = (entity.data as Record<string, any>)?.address_1;
  const lat = addressData?.geometry?.location?.lat;
  const lng = addressData?.geometry?.location?.lng;
  const roles = (entity.data as any)?.roles;
  const roleLabels = Array.isArray(roles) ? roles.map((roleId: string) => ENTITY_ROLE_OPTIONS.find(r => r.id === roleId)?.label).filter(Boolean) as string[] : [];
  return {
    id: entity.id,
    objectType: 'entity',
    lat: lat ?? undefined,
    lng: lng ?? undefined,
    title: entity.name_1 ?? undefined,
    image: entity.image ?? undefined,
    href: `/entities/${entity.id}`,
    tags: roleLabels,
    data: entity
  };
}
function initiativeToMapObject(initiative: ApiInitiative): MapBrowserObject {
  const addressData = (initiative.data as Record<string, any>)?.address_1;
  const lat = addressData?.geometry?.location?.lat;
  const lng = addressData?.geometry?.location?.lng;
  const tagLabels = (initiative.tags ?? []).map(tagKey => INITIATIVE_TAG_OPTIONS.find(t => t.id === tagKey)?.label).filter(Boolean) as string[];
  return {
    id: initiative.id,
    objectType: 'initiative',
    lat: lat ?? undefined,
    lng: lng ?? undefined,
    title: initiative.name,
    image: (initiative as any).image ?? undefined,
    href: getInitiativeUrl(initiative.type, initiative.id),
    tags: tagLabels,
    data: initiative,
    highlight: getInitiativeHighlight(initiative.startDate, initiative.endDate),
    dateRange: formatEventDateRange(initiative.startDate, initiative.endDate) || undefined
  };
}

// --- Tab content components ---

function EntitiesTab({
  context
}: {
  context: Record<string, any>;
}) {
  const queryClient = useQueryClient();
  const {
    sessionHash
  } = useSessionState();
  const {
    showEntityDetail
  } = useEntityDetailSheet();
  const {
    data: countries = []
  } = useCountries();
  const searchEnabled = context.searchEnabled !== false;
  const visibleFilters: string[] | undefined = context.visibleFilters;
  const filterLayout: 'desktop' | 'mobile' | 'auto' = context.filterLayout ?? 'auto';
  const limit: number = context.limit ?? 40;
  const countryOptions = useMemo(() => countries.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({
    id: c.alpha3,
    label: `${c.emoji} ${c.name}`
  })), [countries]);
  const filterConfigs = useMemo(() => {
    const base = getDirectoryFilterConfigs(countryOptions.length > 0 ? countryOptions : undefined);
    if (!visibleFilters) return base;
    return base.filter(f => visibleFilters.includes(f.key));
  }, [visibleFilters, countryOptions]);
  const {
    values,
    handlers,
    isReady
  } = useUrlSync({
    filters: DIRECTORY_URL_SYNC_FILTERS
  });
  const initialFilters = useMemo(() => buildDirectoryInitialFilters(values), [values.role.join(','), values.capability.join(','), values.specialisation.join(','), values.channelExpertise.join(','), values.geoPresence.join(','), values.geoExpertise.join(',')]);
  const fetcher = useCallback(async (params: {
    search?: string;
    filters: Record<string, string[]>;
    cursor?: string;
    limit: number;
  }): Promise<BrowserFetcherResult<Entity>> => {
    const hubOptionalTags = context.hubId ? [`@hub=${context.hubId}`] : undefined;
    const query = buildDirectoryApiQuery({
      ...params,
      sessionHash,
      tags: context.tags,
      optionalSystemTags: hubOptionalTags,
      tagExpression: context.tagExpression
    });
    const response = await queryClient.fetchQuery({
      queryKey: ['entities', query],
      queryFn: () => entitiesApi.getEntities(query),
      staleTime: 1000 * 10
    });
    return {
      objects: response.entities,
      hasNextPage: !!response.pagination.nextCursor,
      nextCursor: response.pagination.nextCursor ?? undefined
    };
  }, [sessionHash, context.tags, context.tagExpression, queryClient]);
  const handleSearchChange = useCallback((search: string) => handlers.search(search), [handlers.search]);
  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => syncDirectoryFilters(filters, handlers), [handlers.role, handlers.capability, handlers.specialisation, handlers.channelExpertise, handlers.geoPresence, handlers.geoExpertise]);
  const handleShowDetail = useCallback((obj: MapBrowserObject, allObjects?: MapBrowserObject[]) => {
    if (obj.objectType === 'entity') {
      const entity = obj.data as Entity;
      const entityObjects = (allObjects || []).filter(o => o.objectType === 'entity');
      const navigationList = entityObjects.length > 1 ? buildEntityNavigationList(entityObjects.map(o_0 => o_0.data as Entity), entity.type || 'brand') : undefined;
      showEntityDetail(entity.id, entity.type || 'brand', {
        title: entity.name_1,
        navigationList
      });
    }
  }, [showEntityDetail]);
  if (!isReady) return <HubLoadingSpinner />;
  return <ObjectBrowser<Entity> fetcher={fetcher} fetchKey={`${Array.isArray(context.tags) ? context.tags.join('|') : ''}|${context.tagExpression ? JSON.stringify(context.tagExpression) : ''}`} renderCard={entity_0 => <EntityCard entity={entity_0} entityType={entity_0.type} variant="compact" showRoles />} getKey={entity_1 => entity_1.id} filters={filterConfigs} searchEnabled={searchEnabled} searchPlaceholder="Search..." limit={limit} emptyState={{
    title: 'No matches',
    description: 'Try adjusting your search or filters'
  }} initialSearch={values.search} initialFilters={initialFilters} onSearchChange={handleSearchChange} onFiltersChange={handleFiltersChange} filterLayout={filterLayout} toMapObject={entityToMapObject} mapConfig={{
    defaultShowMap: true,
    showMapOnEmpty: true,
    onShowDetail: handleShowDetail,
    ...context.mapSettings
  }} />;
}
function InitiativesTab({
  context,
  scope
}: {
  scope: string;
  context: Record<string, any>;
}) {
  const queryClient = useQueryClient();
  const {
    data: userSession
  } = useSession();
  const isAdmin = !!userSession?.user?.isAdmin;
  const {
    showInitiativeDetail
  } = useInitiativeDetailSheet();
  const searchEnabled = context.searchEnabled !== false;
  const visibleFilters: string[] | undefined = context.visibleFilters;
  const filterLayout: 'desktop' | 'mobile' | 'auto' = context.filterLayout ?? 'auto';
  const limit: number = context.limit ?? 40;
  const scopeInfo = useMemo(() => {
    switch (scope) {
      case 'trade':
        return {
          scope: 'trade',
          title: 'Opportunities for Brands, Trade Partners and Places'
        };
      case 'consumer':
        return {
          scope: 'consumer',
          title: 'Things for me',
          details: 'Offers, experiences, events, promotions'
        };
      default:
        return {
          title: 'Offers and Experiences'
        };
    }
  }, [scope]);
  const categories = useMemo(() => {
    // When context.tags is specified, tags are the primary membership mechanism
    // Don't apply category scoping on top (it would over-restrict)
    if (context.tags && context.tags.length > 0) return undefined;
    if (context.categories) return context.categories;else if (scopeInfo?.scope === 'trade') return ['trade'];else if (scopeInfo?.scope === 'consumer') return ['consumer'];
    return undefined;
  }, [scopeInfo?.scope, context?.categories, context?.tags]);
  const handleShowDetail = useCallback((obj: MapBrowserObject, allObjects?: MapBrowserObject[]) => {
    if (obj.objectType === 'initiative') {
      const initiativeObjects = (allObjects || []).filter(o => o.objectType === 'initiative');
      const navigationList = initiativeObjects.length > 1 ? buildInitiativeNavigationList(initiativeObjects.map(o_0 => o_0.data as ApiInitiative)) : undefined;
      showInitiativeDetail(obj.data as ApiInitiative, {
        navigationList
      });
    }
  }, [showInitiativeDetail]);
  const {
    values,
    handlers,
    isReady
  } = useUrlSync({
    filters: WHATSON_URL_SYNC_FILTERS
  });
  const dateRange: DateRange | undefined = values.dateFrom || values.dateTo ? {
    from: values.dateFrom ? new Date(values.dateFrom) : undefined,
    to: values.dateTo ? new Date(values.dateTo) : undefined
  } : undefined;
  const handleDateRangeChange = useCallback((newDateRange: DateRange | undefined) => {
    handlers.dateFrom(newDateRange?.from?.toISOString() || '');
    handlers.dateTo(newDateRange?.to?.toISOString() || '');
  }, [handlers.dateFrom, handlers.dateTo]);
  const filterConfigs = useMemo(() => {
    const base = getWhatsOnFilterConfigs(isAdmin);
    if (!visibleFilters) return base;
    return base.filter(f => visibleFilters.includes(f.key));
  }, [isAdmin, visibleFilters]);
  const initialFilters = useMemo(() => buildWhatsOnInitialFilters(values, isAdmin), [values.categories.join(','), values.type.join(','), values.activities.join(','), values.occasions.join(','), values.status.join(','), isAdmin]);
  const fetcher = useCallback(async (params: {
    search?: string;
    filters: Record<string, string[]>;
    cursor?: string;
    limit: number;
  }): Promise<BrowserFetcherResult<ApiInitiative>> => {
    const hubOptionalTags = context.hubId ? [`@hub=${context.hubId}`] : undefined;
    const query = buildWhatsOnApiQuery({
      ...params,
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      categories,
      tags: context.tags,
      tagExpression: context.tagExpression,
      optionalSystemTags: hubOptionalTags,
      dateRequired: context.dateRequired,
      includePast: context.includePast,
      interested: values.interested === 'true',
      assigned: values.assigned === 'true'
    });
    const response = await queryClient.fetchQuery({
      queryKey: ['initiatives', query],
      queryFn: () => initiativesApi.getInitiatives(query),
      staleTime: 1000 * 10
    });
    return {
      objects: response.initiatives,
      hasNextPage: !!response.pagination?.nextCursor,
      nextCursor: response.pagination?.nextCursor ?? undefined
    };
  }, [values.dateFrom, values.dateTo, values.interested, values.assigned, context.tags, context.tagExpression, context.hubId, context.dateRequired, context.includePast, categories, queryClient]);
  const handleSearchChange = useCallback((search: string) => handlers.search(search), [handlers.search]);
  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => syncWhatsOnFilters(filters, handlers, isAdmin), [handlers.categories, handlers.type, handlers.activities, handlers.occasions, handlers.status, isAdmin]);
  const whenDatePresents = useMemo(() => {
    return RelativeDatePresents.filter(p => !p.id.startsWith('last') && p.id !== 'yesterday');
  }, []);
  const dateChip = useMemo(() => {
    if (!dateRange?.from && !dateRange?.to) return null;
    const fromStr = dateRange?.from ? format(dateRange.from, 'MMM d, yyyy') : '';
    const toStr = dateRange?.to ? format(dateRange.to, 'MMM d, yyyy') : '';
    const label = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
        {label}
        <button onClick={() => handleDateRangeChange(undefined)} className="hover:text-primary-900 cursor-pointer">
          <X className="w-3 h-3" />
        </button>
      </span>;
  }, [dateRange, handleDateRangeChange]);
  if (!isReady) return <HubLoadingSpinner />;

  // Quick-filter checkboxes — shown above the browser when configured via context
  const quickFilterOptions: string[] = context.quickFilters || [];
  const showQuickFilters = quickFilterOptions.length > 0 && !!userSession?.user;
  return <div>
      {showQuickFilters && <div className="flex flex-wrap items-center gap-3 mb-3">
          {quickFilterOptions.includes('interested') && <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={values.interested === 'true'} onChange={e => handlers.interested(e.target.checked ? 'true' : '')} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" />
              <span className="text-gray-700">I&apos;m interested</span>
            </label>}
          {quickFilterOptions.includes('assigned') && <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={values.assigned === 'true'} onChange={e_0 => handlers.assigned(e_0.target.checked ? 'true' : '')} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" />
              <span className="text-gray-700">I&apos;m assigned</span>
            </label>}
        </div>}
      <ObjectBrowser<ApiInitiative> fetcher={fetcher} fetchKey={`${values.dateFrom}|${values.dateTo}|${values.interested}|${values.assigned}|${Array.isArray(context.tags) ? context.tags.join(',') : ''}|${context.tagExpression ? JSON.stringify(context.tagExpression) : ''}`} filterSlot={<div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">When</label>
            <div className="flex flex-wrap items-center gap-2">
              <DatePickerWithRange presets={whenDatePresents} date={dateRange} setDate={handleDateRangeChange} clearable={true} />
              {dateRange && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 cursor-pointer" onClick={() => handleDateRangeChange(undefined)} title="Clear date range">
                  <X className="h-4 w-4" />
                </Button>}
            </div>
          </div>} renderCard={initiative => <InitiativeCard showOwner={context.showOwner ?? true} initiative={initiative} variant="compact" showStatus={context.showStatus} showInterestCount={context.showInterestCount} showUserInterested={context.showUserInterested} showUserAssignments={context.showUserAssignments} />} getKey={initiative_0 => initiative_0.id} filters={filterConfigs} searchEnabled={searchEnabled} searchPlaceholder="Search..." limit={limit} emptyState={{
      title: 'No matches',
      description: 'Try adjusting your search, filters, or date range'
    }} initialSearch={values.search} initialFilters={initialFilters} onSearchChange={handleSearchChange} onFiltersChange={handleFiltersChange} filterLayout={filterLayout} filterChipSlot={dateChip} toMapObject={initiativeToMapObject} mapConfig={{
      defaultShowMap: true,
      showMapOnEmpty: true,
      onShowDetail: handleShowDetail,
      legend: INITIATIVE_TIMING_LEGEND,
      ...context.mapSettings
    }} />
    </div>;
}
function ContentTab({
  context,
  backLabel,
  backHref
}: {
  context: Record<string, any>;
  backLabel?: string;
  backHref?: string;
}) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const {
    values,
    handlers,
    isReady
  } = useUrlSync({
    filters: {
      search: {
        paramName: 'search',
        defaultValue: ''
      },
      type: {
        paramName: 'type',
        defaultValue: [],
        isArray: true
      }
    }
  });
  const initialFilters = useMemo(() => {
    const f: Record<string, string[]> = {};
    if (values.type.length > 0) f.type = values.type;
    return f;
  }, [values.type.join(',')]);
  const handleSearchChange = useCallback((search: string) => handlers.search(search), [handlers.search]);
  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => {
    handlers.type(filters.type || []);
  }, [handlers.type]);
  if (!isReady) return <HubLoadingSpinner />;
  return <div className="max-w-[1000px] mx-auto">
      <ContentBrowser fetchKey={`${Array.isArray(context.tags) ? context.tags.join('|') : ''}|${context.tagExpression ? JSON.stringify(context.tagExpression) : ''}`} query={{
      type: context.contentTypes || ['news', 'article', 'announcement'],
      status: 'published',
      visibility: 'public',
      ownerId: context.ownerId,
      ownerType: context.ownerType,
      tags: context.tags,
      tagExpression: context.tagExpression
    }} searchPlaceholder={context.searchPlaceholder || 'Search news...'} showTypeFilter={true} filterLayout={context.filterLayout || 'mobile'} showResultCount={context.showResultCount !== false} limit={context.limit || 20} emptyState={context.emptyState || {
      title: 'No news yet',
      description: 'Check back soon'
    }} initialSearch={values.search} initialFilters={initialFilters} onSearchChange={handleSearchChange} onFiltersChange={handleFiltersChange} backLabel={backLabel} backHref={backHref} viewMode={viewMode} onViewModeChange={setViewMode} renderListItem={content => <ContentListItem content={content} backLabel={backLabel} backHref={backHref} />} />
    </div>;
}
function CollectionsTab(t0) {
  const $ = _c(9);
  const {
    context
  } = t0;
  const limit = context.limit ?? 20;
  const canFilter = context.canFilter !== false;
  const includeProfiled = context.includeProfiled ?? true;
  let t1;
  if ($[0] !== context.tags || $[1] !== includeProfiled || $[2] !== limit) {
    t1 = {
      tags: context.tags,
      limit,
      includeProfiled
    };
    $[0] = context.tags;
    $[1] = includeProfiled;
    $[2] = limit;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const query = t1;
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      tagsEnabled: false
    };
    t3 = {
      title: "No collections yet",
      description: "Collections will appear here once they are created."
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== canFilter || $[7] !== query) {
    t4 = <CollectionGridView query={query} variant="full" isOwner={false} linkCard={true} canFilter={canFilter} filterViewProps={t2} emptyState={t3} />;
    $[6] = canFilter;
    $[7] = query;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ItemsTab(t0) {
  const $ = _c(13);
  const {
    context
  } = t0;
  const limit = context.limit ?? 40;
  const searchEnabled = context.searchEnabled !== false;
  const brandId = context.brandId;
  const lockedTags = Array.isArray(context.lockedTags) ? context.lockedTags : undefined;
  let t1;
  if ($[0] !== context.tags || $[1] !== lockedTags) {
    t1 = lockedTags ?? (Array.isArray(context.tags) ? context.tags : []);
    $[0] = context.tags;
    $[1] = lockedTags;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const tags = t1;
  const canFilter = lockedTags ? false : context.canFilter !== false;
  const t2 = context.emptyTitle || "No products found";
  const t3 = context.emptyDescription || "Check back soon for new products";
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      title: t2,
      description: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== brandId || $[7] !== canFilter || $[8] !== limit || $[9] !== searchEnabled || $[10] !== t4 || $[11] !== tags) {
    t5 = <ItemGridView brandId={brandId} applySessionHash={true} searchEnabled={searchEnabled} canFilter={canFilter} initialSelectedTags={tags} limit={limit} variant="embedded" emptyState={t4} />;
    $[6] = brandId;
    $[7] = canFilter;
    $[8] = limit;
    $[9] = searchEnabled;
    $[10] = t4;
    $[11] = tags;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function InspirationTab(t0) {
  const $ = _c(10);
  const {
    context
  } = t0;
  const lockedFilters = context.lockedFilters as LockedInspirationFilters | undefined;
  const visibleFilters = Array.isArray(context.visibleFilters) ? context.visibleFilters as InspirationFilterKey[] : undefined;
  const showLockedFilters = context.showLockedFilters !== false;
  const pageSize = typeof context.limit === "number" ? context.limit : undefined;
  const showQuickFilters = context.showQuickFilters !== false;
  const quickFiltersPosition = context.quickFiltersPosition === "above" ? "above" : context.quickFiltersPosition === "withFilters" ? "withFilters" : "below";
  let t1;
  bb0: {
    if (!Array.isArray(context.quickFilters)) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[0] !== context.quickFilters) {
      t2 = parseQuickFilterPresets(context.quickFilters, INSPIRATION_FILTER_KEYS) ?? undefined;
      $[0] = context.quickFilters;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = t2;
  }
  const quickFilters = t1;
  let t2;
  if ($[2] !== lockedFilters || $[3] !== pageSize || $[4] !== quickFilters || $[5] !== quickFiltersPosition || $[6] !== showLockedFilters || $[7] !== showQuickFilters || $[8] !== visibleFilters) {
    t2 = <InspirationFeed hideHeader={true} lockedFilters={lockedFilters} visibleFilters={visibleFilters} showLockedFilters={showLockedFilters} pageSize={pageSize} showQuickFilters={showQuickFilters} quickFiltersPosition={quickFiltersPosition} quickFilters={quickFilters} />;
    $[2] = lockedFilters;
    $[3] = pageSize;
    $[4] = quickFilters;
    $[5] = quickFiltersPosition;
    $[6] = showLockedFilters;
    $[7] = showQuickFilters;
    $[8] = visibleFilters;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function HubLoadingSpinner() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// --- Main HubView component ---

export function HubView({
  id,
  title,
  context,
  tabs: allTabs,
  tooltipsDisabled,
  content,
  backLabel,
  backHref,
  follow,
  tagFilter,
  tagFilterLabel,
  tagFilterAlign = 'left',
  tagFilterOverflow = 'wrap',
  tagFilterTabs
}: HubViewProps) {
  const router = useRouter();
  const {
    data: sessionData
  } = useSession();
  const {
    values,
    isReady
  } = useUrlSync({
    filters: {
      tab: {
        paramName: 'tab',
        defaultValue: 'home'
      },
      tags: {
        paramName: 'tags',
        defaultValue: [],
        isArray: true
      }
    }
  });

  // Exclude disabled tabs so they are never shown or mounted.
  const tabs = useMemo(() => allTabs.filter(t => !t.disabled), [allTabs]);
  const activeTabId = values.tab || 'home';
  const activeTab = tabs.find(t_0 => t_0.id === activeTabId) ?? tabs[0];

  // Whether the global tag filter row should render on the active tab. Defaults
  // to all tabs; tagFilterTabs restricts it to the listed tab ids.
  const showTagFilterOnActiveTab = !tagFilterTabs || (activeTab ? tagFilterTabs.includes(activeTab.id) : false);

  // --- Global tag filter state ---
  const selectedTagKeys = values.tags || [];

  // Resolve selected tag keys into actual tag values (flattening arrays)
  const resolvedSelectedTags = useMemo(() => {
    if (!tagFilter || selectedTagKeys.length === 0) return [];
    const tags: string[] = [];
    for (const key of selectedTagKeys) {
      const option = tagFilter.find(o => Array.isArray(o.value) ? o.value.join(',') === key : o.value === key);
      if (option) {
        if (Array.isArray(option.value)) {
          tags.push(...option.value);
        } else {
          tags.push(option.value);
        }
      }
    }
    return tags;
  }, [tagFilter, selectedTagKeys.join(',')]);

  // Put selected filter tags into context.tagExpression (separate from context.tags)
  // This produces an independent { any: [...] } expression that the API applies additively.
  const resolvedContext = useMemo(() => {
    if (resolvedSelectedTags.length === 0) return context;
    return {
      ...context,
      tagExpression: {
        any: resolvedSelectedTags
      }
    };
  }, [context, resolvedSelectedTags]);
  const handleTagToggle = useCallback((optionKey: string) => {
    const current = selectedTagKeys;
    const next = current.includes(optionKey) ? current.filter(k => k !== optionKey) : [...current, optionKey];

    // Preserve existing query params (tab, dynamic route params) and update tags
    const dynamicParams: Record<string, string | string[]> = {};
    const pathSegments = router.pathname.match(/\[(\w+)\]/g);
    if (pathSegments) {
      for (const seg of pathSegments) {
        const key_0 = seg.slice(1, -1);
        if (router.query[key_0]) {
          dynamicParams[key_0] = String(router.query[key_0]);
        }
      }
    }
    const query: Record<string, string | string[]> = {
      ...dynamicParams
    };
    if (values.tab && values.tab !== 'home') {
      query.tab = values.tab;
    }
    if (next.length > 0) {
      query.tags = next;
    }
    router.push({
      pathname: router.pathname,
      query
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }, [selectedTagKeys, values.tab, router]);

  // Track which tabs have been visited so we lazy-mount on first show,
  // but keep them in the DOM (hidden) once mounted.
  const [mountedTabs, setMountedTabs] = useState<Set<string>>(new Set());

  // Ensure the active tab is in the mounted set
  const activeMountedTabs = useMemo(() => {
    const activeId = activeTab?.id;
    if (!activeId || mountedTabs.has(activeId)) return mountedTabs;
    const next_0 = new Set(mountedTabs);
    next_0.add(activeId);
    return next_0;
  }, [activeTab?.id, mountedTabs]);

  // Sync state if the set changed
  if (activeMountedTabs !== mountedTabs) {
    setMountedTabs(activeMountedTabs);
  }
  const handleTabChange = useCallback((tabId: string) => {
    // Clear all query params except 'tab' and 'tags' so each tab starts fresh.
    // Browser back/forward will restore the full URL state naturally.
    // Preserve dynamic route params (e.g. [id]) so Next.js can interpolate the pathname.
    const dynamicParams_0: Record<string, string | string[]> = {};
    const pathSegments_0 = router.pathname.match(/\[(\w+)\]/g);
    if (pathSegments_0) {
      for (const seg_0 of pathSegments_0) {
        const key_1 = seg_0.slice(1, -1);
        if (router.query[key_1]) {
          dynamicParams_0[key_1] = String(router.query[key_1]);
        }
      }
    }
    const query_0: Record<string, string | string[]> = {
      ...dynamicParams_0
    };
    if (tabId !== 'home') {
      query_0.tab = tabId;
    }
    // Preserve selected tags across tab changes
    if (selectedTagKeys.length > 0) {
      query_0.tags = selectedTagKeys;
    }
    router.push({
      pathname: router.pathname,
      query: query_0
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }, [router, selectedTagKeys]);
  const isBannerVisible = useEarlyPreviewBanner();
  if (!isReady) {
    return <div className="min-h-[400] bg-gray-50 flex items-center justify-center">
        <HubLoadingSpinner />
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2">
        {/* Sticky title + tabs */}
        <div className="sticky z-40 bg-gradient-to-b from-gray-50 to-gray-50 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 pt-2" style={{
        top: getFixedChromeOffset(isBannerVisible)
      }}>
          {title && <div className="flex items-center justify-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900 text-center">{title}</h1>
              {follow && <FollowButton objectId={follow.objectId} objectType={follow.objectType} following={follow.following} showLabel />}
            </div>}

          {/* Tab bar */}
          <TooltipProvider>
            <nav className="flex overflow-x-auto gap-1 border-b border-gray-200 mb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" aria-label="Hub tabs">
              <div className="flex gap-1 mx-auto px-3">
                {tabs.map(tab => {
                const isActive = tab.id === activeTab?.id;
                const tabButton = <button key={tab.id} onClick={() => handleTabChange(tab.id)} className={cn('px-2 py-2 text-sm font-medium cursor-pointer transition-colors relative whitespace-nowrap shrink-0', 'hover:text-primary-600', isActive ? 'text-primary-600 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary-600' : 'text-gray-500')} aria-selected={isActive} aria-label={!tab.title && tab.tooltip ? tab.tooltip : undefined} role="tab">
                      <span className="inline-flex items-center gap-1.5">
                        {tab.icon}
                        {tab.title}
                      </span>
                    </button>;
                if (tab.tooltip && !tooltipsDisabled) {
                  return <Tooltip key={tab.id}>
                        <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
                        <TooltipContent>{tab.tooltip}</TooltipContent>
                      </Tooltip>;
                }
                return tabButton;
              })}
              </div>
            </nav>
          </TooltipProvider>

          {/* Global tag filter */}
          {tagFilter && tagFilter.length > 0 && showTagFilterOnActiveTab && <div className={cn('flex items-center gap-2 pb-2', tagFilterAlign === 'center' && 'mx-auto w-fit max-w-full', tagFilterAlign === 'right' && 'ml-auto w-fit max-w-full')}>
              {tagFilterLabel && <span className="text-sm font-medium text-gray-800 mr-1 shrink-0">
                  {tagFilterLabel}
                </span>}
              <div className={cn('relative min-w-0', tagFilterOverflow === 'scroll' && 'overflow-hidden')}>
                <div className={cn('flex items-center gap-2', tagFilterOverflow === 'wrap' && 'flex-wrap', tagFilterOverflow === 'scroll' && 'overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden')}>
                  {tagFilter.map(option_0 => {
                const optionKey_0 = Array.isArray(option_0.value) ? option_0.value.join(',') : option_0.value;
                const isSelected = selectedTagKeys.includes(optionKey_0);
                return <button key={optionKey_0} onClick={() => handleTagToggle(optionKey_0)} className={cn('px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0', isSelected ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                        {option_0.label}
                      </button>;
              })}
                  {selectedTagKeys.length > 0 && <button onClick={() => {
                const dynamicParams_1: Record<string, string | string[]> = {};
                const pathSegments_1 = router.pathname.match(/\[(\w+)\]/g);
                if (pathSegments_1) {
                  for (const seg_1 of pathSegments_1) {
                    const key_2 = seg_1.slice(1, -1);
                    if (router.query[key_2]) {
                      dynamicParams_1[key_2] = String(router.query[key_2]);
                    }
                  }
                }
                const query_1: Record<string, string | string[]> = {
                  ...dynamicParams_1
                };
                if (values.tab && values.tab !== 'home') {
                  query_1.tab = values.tab;
                }
                router.push({
                  pathname: router.pathname,
                  query: query_1
                }, undefined, {
                  shallow: true,
                  scroll: false
                });
              }} className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap shrink-0">
                      <X className="w-3 h-3 inline mr-0.5" />
                      Clear
                    </button>}
                </div>
                {/* Scroll fade indicator on right edge */}
                {tagFilterOverflow === 'scroll' && <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-gray-50 to-transparent" />}
              </div>
            </div>}
        </div>

        {/* Tab description — only show for active tab */}
        {activeTab?.description && <div className="mt-4 mb-4">
            <Markdown className="text-sm text-gray-500">{activeTab.description}</Markdown>
          </div>}

        {/* Tab content — lazy mount on first visit, then hide with CSS */}
        {content && <div className="mt-4 mb-6">{content}</div>}
        {tabs.map(tab_0 => {
        if (!activeMountedTabs.has(tab_0.id)) return null;
        const isActive_0 = tab_0.id === activeTab?.id;
        return <div key={tab_0.id} role="tabpanel" className={isActive_0 ? undefined : 'hidden'} aria-hidden={!isActive_0}>
              <HubTabContent view={tab_0.view} context={tab_0.context ? {
            ...resolvedContext,
            ...tab_0.context
          } : resolvedContext} component={tab_0.component} backLabel={backLabel} backHref={backHref} />
            </div>;
      })}
      </div>
    </div>;
}
function HubTabContent(t0) {
  const $ = _c(20);
  const {
    view,
    context,
    component: Component,
    backLabel,
    backHref
  } = t0;
  switch (view) {
    case "directory":
    case "entities":
      {
        let t1;
        if ($[0] !== context) {
          t1 = <EntitiesTab context={context} />;
          $[0] = context;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "whatson":
      {
        let t1;
        if ($[2] !== context) {
          t1 = <InitiativesTab scope="consumer" context={context} />;
          $[2] = context;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "opportunities":
      {
        let t1;
        if ($[4] !== context) {
          t1 = <InitiativesTab scope="trade" context={context} />;
          $[4] = context;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "news":
    case "content":
      {
        let t1;
        if ($[6] !== backHref || $[7] !== backLabel || $[8] !== context) {
          t1 = <ContentTab context={context} backLabel={backLabel} backHref={backHref} />;
          $[6] = backHref;
          $[7] = backLabel;
          $[8] = context;
          $[9] = t1;
        } else {
          t1 = $[9];
        }
        return t1;
      }
    case "collections":
      {
        let t1;
        if ($[10] !== context) {
          t1 = <CollectionsTab context={context} />;
          $[10] = context;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        return t1;
      }
    case "inspiration":
      {
        let t1;
        if ($[12] !== context) {
          t1 = <InspirationTab context={context} />;
          $[12] = context;
          $[13] = t1;
        } else {
          t1 = $[13];
        }
        return t1;
      }
    case "items":
      {
        let t1;
        if ($[14] !== context) {
          t1 = <ItemsTab context={context} />;
          $[14] = context;
          $[15] = t1;
        } else {
          t1 = $[15];
        }
        return t1;
      }
    case "members":
      {
        let t1;
        if ($[16] !== context) {
          t1 = <MembersTab context={context} />;
          $[16] = context;
          $[17] = t1;
        } else {
          t1 = $[17];
        }
        return t1;
      }
    case "home":
    default:
      {
        let t1;
        if ($[18] !== Component) {
          t1 = Component ? <Component /> : <div className="text-center py-12 text-gray-400" />;
          $[18] = Component;
          $[19] = t1;
        } else {
          t1 = $[19];
        }
        return t1;
      }
  }
}